<template>
  <kst-table action input-mode :data="userItems">
    <b-table-column cell-class="ks-col-nopad" header-class="p-0" width="1">
      <template #header>
        <kst-header-blank />
      </template>
      <template #default="{ row }">
        <kst-tooltip v-if="row.Errors.length > 0" error-mode>
          <template #content>
            <kst-list :items="row.Errors"/>
          </template>
          <kst-icon error-mode/>
        </kst-tooltip>
      </template>
    </b-table-column>

    <b-table-column
      v-slot="{ column, row, index }"
      field="DispatchName"
      :label="SlsItemCatalog.DispatchName.Label"
    >
      <kst-input error-off field-only
        :field="column.field" :data="row" :catalog="SlsItemCatalog"
        :disabled="row.IsClosed" :index="index" @error="handleRowErrors"
      />
    </b-table-column>

    <b-table-column cell-class="ks-col-nopad" header-class="p-0" width="1">
      <template #header>
        <kst-header-blank />
      </template>
      <template #default="{ row }">
        <kst-tooltip info-mode>
          <template #content>
            <kst-list info-mode :items="Model.Edit.getItemInfo(row)"/>
          </template>
          <kst-icon info-mode/>
        </kst-tooltip>
      </template>
    </b-table-column>

    <b-table-column
      v-slot="{ column, row, index }"
      cell-class="ks-col-packaging"
      field="PackagingName"
      :label="SlsItemCatalog.PackagingName.Label"
    >
      <template v-if="row.DispatchIsActive && row.QtySalesOrder === 0">
        <kst-input error-off field-only
          :field="column.field" :data="row" :catalog="SlsItemCatalog"
          :index="index" :options="row.PackagingOptions"
          @input="handlePackaging(row)" @error="handleRowErrors"
        />
      </template>
      <template v-else>
        <kst-output box-off field-only
          field="Packaging" :data="row" :catalog="SlsItemCatalog"
        />
      </template>
    </b-table-column>

    <b-table-column
      v-slot="{ column, row, index }"
      cell-class="ks-col-qty"
      field="Qty"
      :label="SlsItemCatalog.Qty.Label"
    >
      <kst-input error-off field-only
        :field="column.field" :data="row" :catalog="SlsItemCatalog"
        :index="index" @input="handleQuantity(row)" @error="handleRowErrors"
      />
    </b-table-column>

    <b-table-column
      v-slot="{ column, row, index }"
      cell-class="ks-col-price-button"
      field="SellPrice"
      :label="SlsItemCatalog.SellPrice.Label"
    >
      <kst-input error-off field-only
        :field="column.field" :data="row" :catalog="SlsItemCatalog"
        :disabled="row.IsClosed" :index="index"
        @input="handleSellPrice(row)" @error="handleRowErrors"
      >
        <template #right>
          <div class="control">
            <kst-tooltip search-mode hover-only
              position="is-bottom"
              :content="SlsItemCatalog.SellPrice.Label"
            >
              <kst-button search-mode @click="handleSearch_Price(index)"/>
            </kst-tooltip>
          </div>
        </template>
      </kst-input>
    </b-table-column>

    <b-table-column
      v-if="hasItemLevelDisc"
      v-slot="{ column, row, index }"
      cell-class="ks-col-disc-type"
      field="DiscType"
      :label="SlsItemCatalog.DiscType.Label"
    >
      <kst-input error-off field-only
        :field="column.field" :data="row" :catalog="SlsItemCatalog"
        :index="index" :options="row.DiscTypeOptions"
        :disabled="row.QtySalesOrder > 0"
        @input="handleDiscType(row)" @error="handleRowErrors"
      />
    </b-table-column>

    <b-table-column
      v-if="hasItemLevelDisc"
      v-slot="{ column, row, index }"
      cell-class="ks-col-disc-value"
      field="DiscValue"
      :label="SlsItemCatalog.ItemDiscValue.Label"
    >
      <template v-if="row.DiscType === Model.DiscType.Value.ID">
        <kst-input error-off field-only
          field="DiscValue" :data="row" :catalog="SlsItemCatalog"
          :index="index" :disabled="row.QtySalesOrder > 0"
          @input="handleDiscValue(row)" @error="handleRowErrors"
        >
        </kst-input>
      </template>
      <template v-else-if="row.DiscType === Model.DiscType.Percent.ID">
        <kst-input error-off field-only
          field="DiscPercent" :data="row" :catalog="SlsItemCatalog"
          :index="index" :disabled="row.QtySalesOrder > 0"
          @input="handleDiscPercent(row)" @error="handleRowErrors"
        />
      </template>
      <template v-else>
        <input class="input is-invisible"/>
      </template>
    </b-table-column>

    <b-table-column numeric
      v-slot="{ column, row }"
      cell-class="ks-col-subtotal"
      field="TotalAfterDisc"
      :label="SlsItemCatalog.Total.Label"
    >
      <kst-output box-off field-only
        :field="column.field" :data="row" :catalog="SlsItemCatalog"
      />
    </b-table-column>

    <b-table-column
      v-slot="{ column, row, index }"
      cell-class="ks-col-isclosed"
      field="IsClosed"
      :label="SlsItemCatalog.IsClosed.Label"
    >
      <kst-input error-off field-only
        :field="column.field" :data="row" :catalog="SlsItemCatalog" :index="index"
        :disabled="isNaN(parseInt(row.Qty)) || parseInt(row.Qty) <= row.QtySalesOrder"
      />
    </b-table-column>

    <template #action="{ row, index }">
      <template v-if="row.QtySalesOrder === 0">
        <kst-tooltip delete-mode hover-only :content="Model.SalesItem.Module.FullName">
          <kst-button delete-mode is-round @click="handleRowDelete(index)"/>
        </kst-tooltip>
      </template>
    </template>

    <template #empty-body>
      <ValidationProvider v-slot="{ errors }" name="userItems" rules="required">
        <kst-error :array-blank-mode="!!errors"/>
      </ValidationProvider>
    </template>
  </kst-table>
</template>

<script>
import DialogMixin from "../../../mixins/DialogMixin.js";
import AppModuleAttr from "../../../services/App/AppModuleAttr.js";
import Catalog from "../SlsCatalog.js";
import Info from "../SlsInfo.js";
import Model from "../SlsModel.js";
import SlsItemCatalog from "../SlsItemCatalog.js";

export default {
  mixins: [DialogMixin],
  props: {
    userData: Object,
    userItems: Array
  },
  data: () => ({
    Catalog: Catalog,
    Model: Model,
    SlsItemCatalog: SlsItemCatalog,
    selectedIndex: null
  }),
  computed: {
    hasItemLevelDisc() {
      return this.$kst.Access.hasModuleAttr(AppModuleAttr.ItemLevelDisc_Act_List);
    }
  },
  methods: {
    handleDiscType(item) {
      if (item.DiscType === Model.DiscType.None.ID) {
        item.DiscValue = "";
        item.DiscPercent = "";
      }

      Model.updateItem(item);
      Model.updateDetails(this.userData, this.userItems);
    },
    handleDiscValue(item) {
      Model.updateItem(item);
      Model.updateDetails(this.userData, this.userItems);
    },
    handleDiscPercent(item) {
      Model.updateItem(item);
      Model.updateDetails(this.userData, this.userItems);
    },
    handlePackaging(item) {
      Model.updateItemByPackaging(item);
      Model.updateItem(item);
      Model.updateDetails(this.userData, this.userItems);
    },
    handleQuantity(item) {
      Model.Edit.validateItem(item);
      Model.updateItem(item);
      Model.updateDetails(this.userData, this.userItems);
    },
    handleSellPrice(item) {
      Model.updateItem(item);
      Model.updateDetails(this.userData, this.userItems);
    },

    handleRowDelete(index) {
      this.showConfirm(
        Info.Message.Edit_ConfirmDeleteItem,
        this.handleRowDelete_Confirm,
        null,
        index
      );
    },
    handleRowDelete_Confirm(index) {
      this.userItems.splice(index, 1);
      Model.updateDetails(this.userData, this.userItems);
    },

    handleRowErrors(errors, field, index) {
      Model.setItemErrors(this.userItems[index], field, errors);
    },

    handleSearch_Price(index) {
      this.$emit(this.$kst.Enum.Event.Callback, index);
    }
  }
}
</script>